import { Suspense, lazy, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "@/components/shared/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { SecurityProvider } from "@/contexts/SecurityContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { MobileOnboarding } from "@/components/shared/common/MobileOnboarding";
import { RealtimeErrorBoundary } from "@/components/shared/common/RealtimeErrorBoundary";
import { I18nextProvider, useTranslation } from "react-i18next";
import i18n from "@/i18n/i18n";
import { ProtectedRoute } from "@/components/shared/auth/ProtectedRoute";
import { PublicRoute } from "@/components/shared/auth/PublicRoute";
import { AppLayout } from "@/components/shared/layout/AppLayout";
import { ThemeProvider } from "@/components/shared/ui/theme-provider";
import "./App.css";

// Lazy load components for better performance
const Index = lazy(() => import("./pages/Index"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LoginForm = lazy(() => import("@/components/shared/auth/LoginForm"));
const UserProfileSettings = lazy(
  () => import("@/components/shared/common/profile/UserProfileSettings")
);
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const SetupPassword = lazy(() => import("@/pages/SetupPassword"));
const Legal = lazy(() => import("./pages/Legal"));
const TermsPublic = lazy(() => import("./pages/TermsPublic"));
const PrivacyPublic = lazy(() => import("./pages/PrivacyPublic"));
const AdminCenter = lazy(() => import("./pages/AdminCenter"));
const AdminUserManagement = lazy(() => import("./pages/AdminUserManagement"));
const TherapistAnalytics = lazy(() => import("./pages/TherapistAnalytics"));

// Therapist sub-views (flattened from TherapistDashboard tabs)
const TherapistOverview = lazy(() =>
  import("@/components/therapist/dashboard/TherapistOverview").then((m) => ({
    default: m.TherapistOverview,
  }))
);
const JourneyBuilderPage = lazy(() =>
  import("@/components/therapist/dashboard/JourneyBuilderPage").then((m) => ({
    default: m.JourneyBuilderPage,
  }))
);
const ClientRoster = lazy(() =>
  import("@/components/therapist/client-management/ClientRoster").then((m) => ({
    default: m.ClientRoster,
  }))
);
const ClientWorkspacePage = lazy(() =>
  import("@/components/therapist/client-management/ClientWorkspacePage").then(
    (m) => ({
      default: m.ClientWorkspacePage,
    })
  )
);
const TemplateLibrary = lazy(() =>
  import("@/components/therapist/templates/TemplateLibrary").then((m) => ({
    default: m.TemplateLibrary,
  }))
);

const ClientDashboard = lazy(() =>
  import("@/components/client/dashboard/ClientDashboard").then((m) => ({
    default: m.ClientDashboard,
  }))
);
const ClientJourneyHistory = lazy(() =>
  import("@/components/client/journey/ClientJourneyHistory").then((m) => ({
    default: m.ClientJourneyHistory,
  }))
);
const ContentLibrary = lazy(
  () => import("@/components/therapist/content/ContentLibrary")
);

// Create a client
// OPTIMIZATION: Longer staleTime in dev to reduce refetch frequency and CPU usage
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: import.meta.env.DEV ? 1000 * 60 * 15 : 1000 * 60 * 5, // 15 min dev, 5 min prod
      retry: (failureCount: number, error: unknown) => {
        // Don't retry on authentication errors
        const status = (error as { status?: number } | undefined)?.status;
        if (status === 401) return false;
        return failureCount < 3;
      },
    },
  },
});

/**
 * PushNotificationInit
 *
 * Re-registers the device token on subsequent logins (after the user has
 * already completed onboarding and granted push permission).  On the very
 * first native launch the MobileOnboarding modal handles the initial prompt.
 *
 * Calling register() when permission is already granted is silent on iOS —
 * the OS does not show a dialog again, it just refreshes the APNs token.
 */
function PushNotificationInit() {
  const { user } = useAuth();
  const { hasCompletedOnboarding } = useOnboardingStatus();
  const { register } = usePushNotifications();
  useEffect(() => {
    // Only silently re-register if the user has been through onboarding.
    // This avoids showing the OS permission prompt without user context.
    if (user?.id && hasCompletedOnboarding) {
      void register();
    }
  }, [user?.id, hasCompletedOnboarding, register]);
  return null;
}

/**
 * NotificationDeepLinkHandler
 *
 * Placed inside <Router> so it can call useNavigate.
 *
 * Responsibilities:
 *   1. Show an in-app toast when a push notification arrives in the foreground.
 *   2. Route the user to the relevant screen when a notification is tapped
 *      (via `pushNotificationActionPerformed`).
 *
 * Deep-link data conventions (set by the send-push-notification Edge Function
 * or the check-task-inactivity scheduler):
 *   data.type     — notification_type enum value
 *   data.taskId   — optional journey_tasks UUID
 *   data.url      — optional arbitrary in-app path
 */
function NotificationDeepLinkHandler() {
  const { user, profile } = useAuth();
  const { lastNotification } = usePushNotifications();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!user || !lastNotification) return;

    const { title, body, data } = lastNotification;

    // Determine deep-link target
    const target = resolveDeepLink(data, profile?.role);

    toast(title ?? t("notifications.newMessage"), {
      description: body,
      ...(target
        ? {
            action: {
              label: t("notifications.view"),
              onClick: () => navigate(target),
            },
          }
        : {}),
    });

    // Auto-navigate on notification tap (data.tapped is set by the action
    // performed listener in usePushNotifications when the user taps)
    if (data?.tapped === "true" && target) {
      navigate(target);
    }
  }, [lastNotification, user, profile?.role, navigate, t]);

  return null;
}

/**
 * Returns the in-app route to navigate to for a given notification payload,
 * or null if no specific target is known.
 */
function resolveDeepLink(
  data: Record<string, string> | undefined,
  role: string | undefined
): string | null {
  if (!data) return null;

  // Explicit URL override (e.g., set by scheduled system notifications)
  if (data.url) return data.url;

  const type = data.type;

  if (role === "client") {
    if (
      type === "task_inactivity" ||
      type === "milestone_encourage" ||
      type === "therapist_nudge"
    ) {
      return "/dashboard/client/journey";
    }
  }

  if (role === "therapist") {
    if (type === "system" && data.clientId) {
      return `/dashboard/therapist/clients/${data.clientId}`;
    }
  }

  return null;
}

function App() {
  return (
    <RealtimeErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <PushNotificationInit />
              <MobileOnboarding />
              <SecurityProvider>
                <Router>
                  <NotificationDeepLinkHandler />
                  <Suspense
                    fallback={
                      <div className="flex items-center justify-center min-h-screen">
                        <div className="flex flex-col items-center gap-3">
                          <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
                        </div>
                      </div>
                    }
                  >
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <PublicRoute>
                            <AppLayout>
                              <Index />
                            </AppLayout>
                          </PublicRoute>
                        }
                      />
                      <Route
                        path="/login"
                        element={
                          <PublicRoute>
                            <AppLayout>
                              <LoginForm />
                            </AppLayout>
                          </PublicRoute>
                        }
                      />
                      <Route
                        path="/reset-password"
                        element={
                          <PublicRoute>
                            <ResetPassword />
                          </PublicRoute>
                        }
                      />
                      <Route
                        path="/setup-password"
                        element={
                          <AppLayout showNav={false}>
                            <SetupPassword />
                          </AppLayout>
                        }
                      />
                      {/* Backward-compatible dashboard entry (role-aware redirect in pages/Dashboard) */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <Dashboard />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Terms (legal acceptance) page (no app shell) */}
                      <Route
                        path="/terms"
                        element={
                          <ProtectedRoute>
                            <Legal />
                          </ProtectedRoute>
                        }
                      />

                      {/* Public, deep-linkable legal pages (Danish paths) */}
                      <Route
                        path="/betingelser"
                        element={
                          <AppLayout>
                            <TermsPublic />
                          </AppLayout>
                        }
                      />
                      <Route
                        path="/privatlivspolitik"
                        element={
                          <AppLayout>
                            <PrivacyPublic />
                          </AppLayout>
                        }
                      />

                      {/* Admin index redirects to legal admin for now */}
                      <Route
                        path="/admin"
                        element={<Navigate to="/admin/legal" replace />}
                      />
                      {/* Client deep links */}
                      <Route
                        path="/dashboard/client"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <Navigate
                                to="/dashboard/client/journey"
                                replace
                              />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/client/journey"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <ClientDashboard />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/client/journey/history"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <ClientJourneyHistory />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Therapist deep links — each sub-view is a direct route */}
                      <Route
                        path="/dashboard/therapist"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <Navigate
                                to="/dashboard/therapist/overview"
                                replace
                              />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/therapist/overview"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <TherapistOverview />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/therapist/journey-builder"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <JourneyBuilderPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/therapist/journey-builder/:journeyId"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <JourneyBuilderPage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/therapist/clients"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <ClientRoster />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/therapist/clients/:clientId"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <ClientWorkspacePage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/therapist/templates"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <TemplateLibrary />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/therapist/analytics"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <TherapistAnalytics />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/therapist/content"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <ContentLibrary />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      {/* Admin - Legal management */}
                      <Route
                        path="/admin/legal"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <AdminCenter />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      {/* Admin - User management */}
                      <Route
                        path="/admin/users"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <AdminUserManagement />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <UserProfileSettings />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/404" element={<NotFound />} />
                      <Route
                        path="*"
                        element={<Navigate to="/404" replace />}
                      />
                    </Routes>
                  </Suspense>
                </Router>
              </SecurityProvider>
            </AuthProvider>
            <Toaster />
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </ThemeProvider>
      </I18nextProvider>
    </RealtimeErrorBoundary>
  );
}

export default App;
